import CircularProgress from '@mui/material/CircularProgress';
import axios from 'axios';
import { useState } from 'react';
import Badge, { BadgeTypes } from 'components/Badge/Badge';
import DotMenuButton from 'components/Button/DotMenuButton';
import DropdownMenu from 'components/Menu/Menu';
import VehicleCard from 'components/VehicleCard/VehicleCard';
import { useAppSettings } from 'context/appsettingsContext';
import { useAuth } from 'context/useAuth';
import { VehicleInterface } from 'types/VehicleInterface';
import './ClientCard.scss';
import { useCollection } from 'functions/hooks/useCollection';
import { documentId, where } from 'firebase/firestore';
import { mapToVehicleInterface } from 'functions/hooks/useGetVehicles';

interface ClientCardProps {
  /** Any data related to the client */
  clientData: {
    active?: boolean;
    email?: string;
    firstName: string;
    id: string;
    idNumber?: string;
    lastName: string;
    makeVariant?: string;
    phoneCode?: string;
    phoneNumber?: string;
    registrationNumber?: string;
    state?: BadgeTypes;
    totalNumberOfVehicles?: number;
    vehicles?: VehicleInterface[];
    tamperActive?: boolean;
  };
  /** State of the device eg. locked, unlocked, etc */
  state?: BadgeTypes;
  /** Pass in HTML or Components as props for actions */
  actionButtons?: React.ReactNode;
  /** Can the card expand when clicking on the title */
  expandable?: boolean;
  /** Boolean flag to fetch vehicles that are active or not. Used in the filter params on getVehicles */
  getActiveDevices?: boolean;
}

/** A UI component used to display client data with expand/collapse functionality */
export default function ClientCard({
  clientData,
  actionButtons,
  expandable = true,
  getActiveDevices = true,
}: ClientCardProps) {
  const {
    id,
    state,
    firstName,
    lastName,
    registrationNumber,
    makeVariant,
    tamperActive,
  } = clientData;

  const [cardExpanded, setCardExpanded] = useState(false);
  const [clientVehicles, setClientVehicles] = useState([]);
  const [loadingClientVehicles, setLoadingClientVehicles] = useState(false);
  const vehiclesCollection = useCollection<any>('vehicles');
  const devicesCollection = useCollection<any>('devices');
  const usersCollection = useCollection<any>('users');

  function handleCardExpanding() {
    if (expandable) {
      setCardExpanded(!cardExpanded);
    }

    if (!cardExpanded) {
      getVehicles();
    }
  }

  async function getVehicles() {

      setLoadingClientVehicles(true);

      try {

        // 1. Fetch only this client's vehicles.
        const vehicles = await vehiclesCollection.queryMany([
          where('userId', '==', clientData.id),
        ]);

        // 2. Collect unique device + driver ids referenced by those vehicles.
        const deviceIds = Array.from(
          new Set(
            vehicles
              .map((v) => v?.data?.deviceId)
              .filter((id: string | undefined): id is string => !!id)
          )
        );
        const driverIds = Array.from(
          new Set(
            vehicles
              .map((v) => v?.data?.driverId)
              .filter((id: string | undefined): id is string => !!id)
          )
        );

        // 3. Fetch devices and drivers in chunked `in` queries (Firestore caps `in` at 10).
        const chunk = <T,>(arr: T[], size: number): T[][] => {
          const out: T[][] = [];
          for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
          return out;
        };

        const devices = (
          await Promise.all(
            chunk(deviceIds, 10).map((ids) =>
              devicesCollection.queryMany([where(documentId(), 'in', ids)])
            )
          )
        ).flat();

        const users = (
          await Promise.all(
            chunk(driverIds, 10).map((ids) =>
              usersCollection.queryMany([where(documentId(), 'in', ids)])
            )
          )
        ).flat();

        const mapped = vehicles
          .map((x) => mapToVehicleInterface(x, devices, users))
          .map((x) => x as never);

        setClientVehicles(mapped);

      } catch (error) {
        console.log(error);
      } finally {
        setLoadingClientVehicles(false);
      }
  }


  const accountMenu = [
    {
      path: `/agent/onboarding/client/${id}/add-driver`,
      label: 'Add a vehicle',
    },
    {
      path: `/agent/clients/${id}/account/view`,
      label: 'View account'
    },
  ];

  return (
    <li className='client-card' aria-label='client card'>
      <div className='client-card__header'>
        <div
          onClick={handleCardExpanding}
          className='client-card__header-title'
          aria-label='Card title'
          style={{ cursor: expandable ? 'pointer' : 'default' }}
        >
          <div className='client-card__header-name'>
            {firstName} {lastName}
          </div>
          {registrationNumber && (
            <div className='client-card__header-vehicle'>
              <span>{registrationNumber.toLocaleUpperCase()}</span>
              <span>{makeVariant}</span>
            </div>
          )}
        </div>

        {actionButtons}
        {tamperActive && <Badge state={'alert'} />}
        {state && <Badge state={state} />}
        <DropdownMenu links={accountMenu} buttonComponent={<DotMenuButton />} />
      </div>
      <div
        className={`client-card__body ${cardExpanded ? 'client-card__body--expanded' : ''
          }`}
        aria-label='Card body'
        aria-expanded={cardExpanded}
      >
        {loadingClientVehicles && <CircularProgress />}
        <div className='vehicleCardContainer'>
          {!loadingClientVehicles && (!clientVehicles.length ? <>No vehicles</> : clientVehicles.map((vehicle: VehicleInterface) => (
              <div className='vehicle' key={vehicle.id}>
                <VehicleCard
                  vehicleId={vehicle.id}
                  deviceId={vehicle.deviceId}
                  deviceSerialNumber={vehicle.deviceSerialNumber}
                  gpsDateTime={vehicle.gpsDateTime}
                  lastDateTime={vehicle.lastDateTime}
                  deviceActive={vehicle.deviceActive}
                  drivers={vehicle.drivers}
                  isLocked={vehicle.isLocked}
                  gps={{
                    lat: vehicle.gpsLatitudeDegrees,
                    lng: vehicle.gpsLongitudeDegrees,
                  }}
                  make={vehicle.make}
                  nickname={vehicle.nickname}
                  registration={vehicle.registrationNumber}
                  tamperActive={vehicle.tamperActive}
                  clientId={clientData.id}
                  isTrackerOnly={vehicle.isTrackerOnly}
                />
              </div>
            )))}
        </div>
      </div>
    </li>
  );
}
